import {
  Award,
  Baby,
  BookOpen,
  Briefcase,
  Brush,
  Building2,
  ChefHat,
  Clapperboard,
  CloudSun,
  Coffee,
  Compass,
  Cpu,
  Dumbbell,
  Film,
  Gamepad2,
  GraduationCap,
  Headset,
  Heart,
  HeartPulse,
  Home,
  Landmark,
  Languages,
  Laugh,
  Leaf,
  LifeBuoy,
  Lightbulb,
  MessageCircle,
  Mountain,
  Music,
  Newspaper,
  Palette,
  PartyPopper,
  PawPrint,
  PiggyBank,
  Plane,
  Repeat,
  Rocket,
  Share2,
  ShieldAlert,
  Shirt,
  Smile,
  Sparkles,
  Sun,
  Target,
  Timer,
  TrendingUp,
  Trophy,
  Users,
  UtensilsCrossed,
  Wallet,
  type LucideIcon,
} from 'lucide-react';

export interface Subtopic {
  readonly ru: string;
  readonly en: string;
  /** Единицы лексики темы — английские слова и устойчивые фразы для практики в диалоге. */
  readonly words: readonly string[];
}

export interface Topic {
  readonly id: string;
  readonly ru: string;
  readonly en: string;
  readonly Icon: LucideIcon;
  readonly subtopics: readonly Subtopic[];
}

/**
 * Каталог тем для свободного разговора — статический набор без бэкенда (см.
 * `TopicRouletteSheet`). 50 широких тем × 5 подтем × ровно 8
 * единиц лексики в каждой — источник: `references/english-topics-50x5x8.md`.
 * Названия хранятся прямо в данных (ru/en), а не через i18n-неймспейс: на 300
 * строк контента отдельный словарь только мешал бы синхронизировать текст.
 */
export const TOPICS = [
  {
    id: 'food',
    ru: 'Еда и кулинария',
    en: 'Food & cooking',
    Icon: UtensilsCrossed,
    subtopics: [
      { ru: 'Покупки продуктов', en: 'Grocery shopping', words: ['grocery store', 'shopping list', 'shopping basket', 'aisle', 'fresh produce', 'checkout', 'receipt', 'special offer'] },
      { ru: 'Готовка дома', en: 'Cooking at home', words: ['recipe', 'ingredients', 'chop', 'boil', 'fry', 'saucepan', 'seasoning', 'serve dinner'] },
      { ru: 'Любимые блюда', en: 'Favourite dishes', words: ['favourite dish', 'flavour', 'spicy', 'sweet', 'filling', 'homemade', 'try something new', 'comfort food'] },
      { ru: 'Ресторан', en: 'At a restaurant', words: ['menu', 'reservation', 'waiter', 'order', 'portion', 'bill', 'tip', 'recommend a dish'] },
      { ru: 'Полезное питание', en: 'Healthy eating', words: ['balanced diet', 'portion size', 'calories', 'sugar', 'vegetables', 'protein', 'cut down on', 'feel full'] },
    ],
  },
  {
    id: 'music',
    ru: 'Музыка',
    en: 'Music',
    Icon: Music,
    subtopics: [
      { ru: 'Любимые жанры', en: 'Favourite genres', words: ['genre', 'rock', 'pop', 'jazz', 'electronic music', 'folk music', 'taste in music', 'listen to'] },
      { ru: 'Концерт', en: 'Concerts', words: ['live show', 'venue', 'crowd', 'ticket', 'stage', 'perform live', 'encore', 'sold out'] },
      { ru: 'Плейлисты', en: 'Playlists', words: ['playlist', 'track', 'shuffle', 'mood', 'headphones', 'put on music', 'skip a song', 'background music'] },
      { ru: 'Тексты песен', en: 'Song lyrics', words: ['lyrics', 'chorus', 'verse', 'meaning', 'catchy', 'sing along', 'know by heart', 'relate to'] },
      { ru: 'Инструменты', en: 'Musical instruments', words: ['guitar', 'piano', 'drums', 'keyboard', 'take lessons', 'practise regularly', 'play by ear', 'musical talent'] },
    ],
  },
  {
    id: 'sports',
    ru: 'Спорт и физическая активность',
    en: 'Sports & fitness',
    Icon: Trophy,
    subtopics: [
      { ru: 'Тренировка', en: 'Workouts', words: ['workout', 'warm-up', 'stretch', 'exercise', 'set', 'repetition', 'personal trainer', 'cool down'] },
      { ru: 'Бег', en: 'Running', words: ['running shoes', 'pace', 'distance', 'route', 'stamina', 'breathe deeply', 'personal best', 'finish line'] },
      { ru: 'Командные игры', en: 'Team games', words: ['teammate', 'coach', 'opponent', 'score', 'pass the ball', 'defence', 'win a match', 'play fair'] },
      { ru: 'Смотреть спорт', en: 'Watching sports', words: ['stadium', 'fan', 'referee', 'half-time', 'final', 'cheer for', 'live broadcast', 'support a team'] },
      { ru: 'Спортивные цели', en: 'Fitness goals', words: ['get stronger', 'build stamina', 'make progress', 'stay active', 'challenge yourself', 'training plan', 'give up', 'keep going'] },
    ],
  },
  {
    id: 'technology',
    ru: 'Технологии',
    en: 'Technology',
    Icon: Cpu,
    subtopics: [
      { ru: 'Смартфон', en: 'Smartphones', words: ['screen', 'battery life', 'charger', 'storage', 'camera', 'lock screen', 'run out of battery', 'turn on'] },
      { ru: 'Приложения', en: 'Apps', words: ['app', 'feature', 'update', 'settings', 'notification', 'install an app', 'delete an app', 'user-friendly'] },
      { ru: 'Искусственный интеллект', en: 'Artificial intelligence', words: ['artificial intelligence', 'chatbot', 'prompt', 'generate', 'reliable', 'make a mistake', 'save time', 'privacy concern'] },
      { ru: 'Гаджеты дома', en: 'Smart home gadgets', words: ['smart speaker', 'device', 'remote control', 'Wi-Fi connection', 'set up', 'voice command', 'plug in', 'stop working'] },
      { ru: 'Безопасность онлайн', en: 'Online safety', words: ['password', 'account', 'personal data', 'privacy', 'permission', 'two-factor login', 'suspicious link', 'protect yourself'] },
    ],
  },
  {
    id: 'travel',
    ru: 'Путешествия',
    en: 'Travel',
    Icon: Plane,
    subtopics: [
      { ru: 'Планирование поездки', en: 'Planning a trip', words: ['destination', 'itinerary', 'travel dates', 'budget', 'book a flight', 'travel insurance', 'visa', 'plan ahead'] },
      { ru: 'Аэропорт', en: 'At the airport', words: ['boarding pass', 'gate', 'luggage', 'carry-on bag', 'security check', 'delayed flight', 'passport control', 'take off'] },
      { ru: 'Жильё', en: 'Accommodation', words: ['hotel room', 'host', 'check-in', 'room key', 'breakfast included', 'reception desk', 'accommodation', 'check out'] },
      { ru: 'Осмотр города', en: 'Sightseeing', words: ['landmark', 'old town', 'guided tour', 'local café', 'map', 'take photos', 'explore on foot', 'get lost'] },
      { ru: 'Проблемы в пути', en: 'Travel problems', words: ['cancelled booking', 'miss a train', 'lost luggage', 'ask for help', 'replacement ticket', 'emergency number', 'wrong address', 'stay calm'] },
    ],
  },
  {
    id: 'work',
    ru: 'Работа и профессия',
    en: 'Work & career',
    Icon: Briefcase,
    subtopics: [
      { ru: 'Рабочий день', en: 'The working day', words: ['workload', 'task', 'priority', 'schedule', 'take a break', 'focus on', 'finish work', 'busy day'] },
      { ru: 'Встречи', en: 'Meetings', words: ['agenda', 'colleague', 'meeting room', 'video call', 'take notes', 'discuss an idea', 'make a decision', 'follow up'] },
      { ru: 'Дедлайны', en: 'Deadlines', words: ['deadline', 'on time', 'delay', 'urgent', 'postpone', 'manage time', 'hand in', 'last minute'] },
      { ru: 'Удалённая работа', en: 'Remote work', words: ['work remotely', 'home office', 'commute', 'flexible hours', 'distraction', 'log in', 'log off', 'work-life balance'] },
      { ru: 'Карьера', en: 'Career', words: ['position', 'experience', 'salary', 'apply for a job', 'interview', 'qualification', 'promotion', 'career goal'] },
    ],
  },
  {
    id: 'education',
    ru: 'Учёба и образование',
    en: 'Education',
    Icon: GraduationCap,
    subtopics: [
      { ru: 'Школьные предметы', en: 'School subjects', words: ['subject', 'maths', 'history', 'science', 'homework', 'lesson', 'favourite teacher', 'difficult subject'] },
      { ru: 'Экзамены', en: 'Exams', words: ['exam', 'revise', 'grade', 'pass', 'fail', 'feel nervous', 'prepare for', 'get feedback'] },
      { ru: 'Онлайн-курсы', en: 'Online courses', words: ['online course', 'module', 'lecture', 'deadline', 'certificate', 'enrol', 'practical task', 'instructor'] },
      { ru: 'Университет', en: 'University', words: ['campus', 'professor', 'degree', 'assignment', 'attend a lecture', 'research paper', 'classmate', 'student life'] },
      { ru: 'Новый навык', en: 'Learning a new skill', words: ['beginner', 'practise', 'make progress', 'improve', 'make mistakes', 'learn from scratch', 'master a skill', 'stay motivated'] },
    ],
  },
  {
    id: 'home',
    ru: 'Дом и быт',
    en: 'Home & everyday life',
    Icon: Home,
    subtopics: [
      { ru: 'Комнаты и интерьер', en: 'Rooms & interior', words: ['kitchen', 'bedroom', 'balcony', 'furniture', 'shelf', 'lamp', 'cosy', 'decorate a room'] },
      { ru: 'Уборка', en: 'Cleaning', words: ['vacuum cleaner', 'do the laundry', 'wash the dishes', 'tidy up', 'take out the rubbish', 'dust', 'cleaning supplies', 'messy'] },
      { ru: 'Ремонт', en: 'Repairs', words: ['repair', 'paint the walls', 'broken tap', 'plumber', 'drill', 'fix it yourself', 'landlord', 'renovation'] },
      { ru: 'Переезд', en: 'Moving house', words: ['moving boxes', 'rent', 'deposit', 'new address', 'unpack', 'move in', 'move out', 'settle in'] },
      { ru: 'Соседи', en: 'Neighbours', words: ['neighbour', 'flat upstairs', 'hallway', 'noise', 'quiet hours', 'complain politely', 'get along', 'borrow something'] },
    ],
  },
  {
    id: 'familyFriends',
    ru: 'Семья и друзья',
    en: 'Family & friends',
    Icon: Users,
    subtopics: [
      { ru: 'Семья', en: 'Family', words: ['relative', 'sibling', 'parents', 'cousin', 'family gathering', 'close to', 'grow up', 'spend time together'] },
      { ru: 'Дружба', en: 'Friendship', words: ['friendship', 'trust', 'support', 'give advice', 'get along', 'have things in common', 'stay in touch', 'close friend'] },
      { ru: 'Семейные традиции', en: 'Family traditions', words: ['tradition', 'celebrate together', 'family recipe', 'holiday meal', 'every year', 'childhood memory', 'pass down', 'special occasion'] },
      { ru: 'Конфликты', en: 'Conflicts', words: ['argument', 'misunderstanding', 'hurt feelings', 'apologise', 'calm down', 'talk it over', 'make up', 'agree to disagree'] },
      { ru: 'Встреча с друзьями', en: 'Meeting up with friends', words: ['invite someone over', 'catch up', 'make plans', 'hang out', 'cancel at the last minute', 'board game', 'go for a walk', 'have a laugh'] },
    ],
  },
  {
    id: 'everydayLife',
    ru: 'Повседневная жизнь',
    en: 'Daily life',
    Icon: Sun,
    subtopics: [
      { ru: 'Утро', en: 'Morning', words: ['alarm clock', 'press snooze', 'feel sleepy', 'get ready', 'make coffee', 'eat breakfast', 'rush out', 'leave home'] },
      { ru: 'Дорога', en: 'Commuting', words: ['commute', 'bus stop', 'traffic jam', 'crowded train', 'miss the bus', 'get off', 'transfer lines', 'arrive late'] },
      { ru: 'Вечер', en: 'Evening', words: ['get home', 'cook dinner', 'do chores', 'watch a series', 'scroll online', 'feel tired', 'unwind', 'go to bed'] },
      { ru: 'Выходной', en: 'Day off', words: ['sleep in', 'run errands', 'make plans', 'lazy day', 'visit friends', 'catch up on sleep', 'stay at home', 'relax'] },
      { ru: 'Привычки', en: 'Habits', words: ['daily routine', 'reminder', 'set a goal', 'do regularly', 'break a habit', 'stick to it', 'track progress', 'make time for'] },
    ],
  },
  {
    id: 'health',
    ru: 'Здоровье и самочувствие',
    en: 'Health & wellbeing',
    Icon: HeartPulse,
    subtopics: [
      { ru: 'Сон', en: 'Sleep', words: ['sleep schedule', 'insomnia', 'fall asleep', 'wake up early', 'pillow', 'bedtime', 'feel rested', 'take a nap'] },
      { ru: 'Простуда', en: 'Having a cold', words: ['sore throat', 'runny nose', 'cough', 'temperature', 'feel unwell', 'stay in bed', 'get better', 'catch a cold'] },
      { ru: 'Визит к врачу', en: 'Doctor\'s visit', words: ['appointment', 'symptom', 'describe the pain', 'clinic', 'check-up', 'prescription', 'treatment', 'recover'] },
      { ru: 'Стресс', en: 'Stress', words: ['feel overwhelmed', 'pressure', 'take a break', 'relax', 'deal with stress', 'breathe deeply', 'mental health', 'coping strategy'] },
      { ru: 'Здоровая привычка', en: 'Healthy habits', words: ['drink water', 'go for a walk', 'balanced diet', 'get enough sleep', 'cut down on sugar', 'daily steps', 'stay active', 'feel better'] },
    ],
  },
  {
    id: 'clothing',
    ru: 'Одежда и стиль',
    en: 'Clothes & style',
    Icon: Shirt,
    subtopics: [
      { ru: 'Покупка одежды', en: 'Buying clothes', words: ['fitting room', 'size', 'try on', 'price tag', 'discount', 'receipt', 'return an item', 'shop assistant'] },
      { ru: 'Повседневный стиль', en: 'Everyday style', words: ['casual clothes', 'jeans', 'hoodie', 'trainers', 'comfortable', 'wear every day', 'simple outfit', 'suit someone'] },
      { ru: 'Официальная одежда', en: 'Formal wear', words: ['dress code', 'formal shirt', 'suit', 'smart shoes', 'special occasion', 'look professional', 'overdressed', 'fit well'] },
      { ru: 'Цвета и сочетания', en: 'Colours & combinations', words: ['bright colour', 'dark shade', 'pattern', 'match colours', 'plain', 'striped', 'go with', 'favourite colour'] },
      { ru: 'Любимая вещь', en: 'A favourite item', words: ['jacket', 'fabric', 'brand', 'worn out', 'quality', 'sentimental value', 'keep for years', 'feel like yourself'] },
    ],
  },
  {
    id: 'weather',
    ru: 'Погода и времена года',
    en: 'Weather & seasons',
    Icon: CloudSun,
    subtopics: [
      { ru: 'Прогноз погоды', en: 'Weather forecast', words: ['weather forecast', 'temperature', 'chance of rain', 'cloudy', 'sunny', 'windy', 'tomorrow morning', 'check the forecast'] },
      { ru: 'Дождливый день', en: 'A rainy day', words: ['umbrella', 'puddle', 'get wet', 'heavy rain', 'raincoat', 'stay indoors', 'grey sky', 'it is pouring'] },
      { ru: 'Жара', en: 'Heatwave', words: ['heatwave', 'humid', 'shade', 'sunscreen', 'drink water', 'air conditioning', 'feel sticky', 'cool down'] },
      { ru: 'Зима', en: 'Winter', words: ['freezing cold', 'snowstorm', 'slippery roads', 'warm layers', 'gloves', 'icy', 'melt', 'stay warm'] },
      { ru: 'Любимый сезон', en: 'Favourite season', words: ['spring flowers', 'autumn leaves', 'long daylight', 'mild weather', 'fresh air', 'seasonal change', 'look forward to', 'favourite time of year'] },
    ],
  },
  {
    id: 'movies',
    ru: 'Фильмы и сериалы',
    en: 'Movies & TV shows',
    Icon: Film,
    subtopics: [
      { ru: 'Выбор фильма', en: 'Choosing a film', words: ['genre', 'trailer', 'rating', 'choose a film', 'recommend', 'worth watching', 'based on a true story', 'streaming service'] },
      { ru: 'Сюжет', en: 'Plot', words: ['plot', 'plot twist', 'ending', 'spoiler', 'predictable', 'surprising', 'make sense', 'keep you guessing'] },
      { ru: 'Персонажи', en: 'Characters', words: ['main character', 'villain', 'hero', 'actor', 'believable', 'relate to', 'character development', 'favourite scene'] },
      { ru: 'Кинотеатр', en: 'At the cinema', words: ['cinema', 'big screen', 'seat', 'subtitles', 'popcorn', 'opening night', 'audience', 'watch at home'] },
      { ru: 'Сериалы', en: 'TV series', words: ['episode', 'season', 'cliffhanger', 'binge-watch', 'next episode', 'release date', 'catch up', 'final season'] },
    ],
  },
  {
    id: 'books',
    ru: 'Книги и чтение',
    en: 'Books & reading',
    Icon: BookOpen,
    subtopics: [
      { ru: 'Любимая книга', en: 'A favourite book', words: ['novel', 'author', 'main character', 'storyline', 'memorable', 'reread', 'recommend a book', 'favourite quote'] },
      { ru: 'Чтение в оригинале', en: 'Reading in the original', words: ['unfamiliar word', 'dictionary', 'reading level', 'understand from context', 'highlight', 'look up', 'easy to read', 'make notes'] },
      { ru: 'Библиотека', en: 'The library', words: ['library card', 'bookshelf', 'borrow a book', 'return date', 'quiet corner', 'librarian', 'overdue', 'reserve a copy'] },
      { ru: 'Нон-фикшн', en: 'Non-fiction', words: ['biography', 'self-help', 'history book', 'facts', 'chapter', 'informative', 'learn something new', 'real-life story'] },
      { ru: 'Привычки чтения', en: 'Reading habits', words: ['e-reader', 'paperback', 'bookmark', 'read before bed', 'turn pages', 'finish a book', 'reading habit', 'carry a book'] },
    ],
  },
  {
    id: 'games',
    ru: 'Игры',
    en: 'Games',
    Icon: Gamepad2,
    subtopics: [
      { ru: 'Видеоигры', en: 'Video games', words: ['console', 'level', 'mission', 'player', 'controller', 'unlock', 'save progress', 'multiplayer'] },
      { ru: 'Настольные игры', en: 'Board games', words: ['board game', 'rules', 'dice', 'take a turn', 'card deck', 'cheat', 'strategy', 'game night'] },
      { ru: 'Игры детства', en: 'Childhood games', words: ['playground', 'hide-and-seek', 'toy', 'make believe', 'after school', 'childhood game', 'used to play', 'invent rules'] },
      { ru: 'Соревнование', en: 'Competition', words: ['opponent', 'score', 'rematch', 'win', 'lose', 'fair play', 'competitive', 'good sport'] },
      { ru: 'Головоломки', en: 'Puzzles', words: ['puzzle', 'clue', 'solve', 'difficult level', 'hint', 'figure out', 'logic', 'satisfying'] },
    ],
  },
  {
    id: 'hobbies',
    ru: 'Хобби и творчество',
    en: 'Hobbies & creativity',
    Icon: Palette,
    subtopics: [
      { ru: 'Рисование', en: 'Drawing', words: ['sketch', 'pencil', 'paint', 'canvas', 'draw from life', 'colour palette', 'creative', 'practise drawing'] },
      { ru: 'Фотография', en: 'Photography', words: ['camera', 'take a shot', 'angle', 'natural light', 'edit a photo', 'portrait', 'landscape', 'capture a moment'] },
      { ru: 'Рукоделие', en: 'Handicrafts', words: ['handmade', 'thread', 'pattern', 'knitting', 'materials', 'make by hand', 'craft project', 'patient work'] },
      { ru: 'Танцы', en: 'Dancing', words: ['rhythm', 'dance move', 'dance class', 'partner', 'practise steps', 'feel the beat', 'perform', 'beginner level'] },
      { ru: 'Творческий проект', en: 'A creative project', words: ['idea', 'inspiration', 'creative block', 'start from scratch', 'improve', 'final result', 'share your work', 'feel proud'] },
    ],
  },
  {
    id: 'nature',
    ru: 'Природа и животные',
    en: 'Nature & animals',
    Icon: PawPrint,
    subtopics: [
      { ru: 'Домашний питомец', en: 'A pet', words: ['pet', 'fur', 'feed', 'walk the dog', 'vet', 'playful', 'adopt', 'take care of'] },
      { ru: 'Дикие животные', en: 'Wild animals', words: ['wildlife', 'species', 'habitat', 'endangered', 'dangerous', 'observe', 'protect nature', 'wild animal'] },
      { ru: 'Прогулка в парке', en: 'A walk in the park', words: ['park bench', 'trees', 'path', 'fresh air', 'peaceful', 'go for a stroll', 'sit outside', 'green space'] },
      { ru: 'Поход', en: 'Hiking', words: ['hike', 'trail', 'backpack', 'tent', 'climb', 'campfire', 'amazing view', 'get tired'] },
      { ru: 'Экологичные привычки', en: 'Eco-friendly habits', words: ['recycle', 'litter', 'reusable bag', 'reduce waste', 'clean up', 'plastic-free', 'protect the environment', 'make a difference'] },
    ],
  },
  {
    id: 'city',
    ru: 'Город и транспорт',
    en: 'City & transport',
    Icon: Building2,
    subtopics: [
      { ru: 'Район', en: 'Neighbourhood', words: ['neighbourhood', 'nearby', 'quiet street', 'convenient', 'local shop', 'safe area', 'live close to', 'move around'] },
      { ru: 'Метро и автобус', en: 'Metro & bus', words: ['subway', 'platform', 'bus stop', 'fare', 'transfer', 'crowded carriage', 'miss the bus', 'get off at'] },
      { ru: 'Пробки', en: 'Traffic jams', words: ['traffic jam', 'rush hour', 'delayed', 'roadworks', 'drive to work', 'stuck in traffic', 'take a shortcut', 'arrive late'] },
      { ru: 'Пешком по городу', en: 'Walking around the city', words: ['pedestrian crossing', 'pavement', 'turn left', 'go straight', 'city centre', 'cross the street', 'walkable', 'shortcut'] },
      { ru: 'Идеальный город', en: 'The ideal city', words: ['public transport', 'green space', 'affordable housing', 'clean streets', 'lively', 'safe at night', 'cycle lanes', 'city life'] },
    ],
  },
  {
    id: 'money',
    ru: 'Деньги и покупки',
    en: 'Money & shopping',
    Icon: Wallet,
    subtopics: [
      { ru: 'Личный бюджет', en: 'Personal budget', words: ['income', 'expense', 'savings', 'monthly budget', 'pay bills', 'afford', 'spend less', 'put money aside'] },
      { ru: 'Онлайн-покупка', en: 'Online shopping', words: ['online store', 'delivery', 'parcel', 'tracking number', 'customer review', 'add to basket', 'place an order', 'return policy'] },
      { ru: 'Скидки', en: 'Discounts', words: ['sale', 'discount', 'bargain', 'price drop', 'special offer', 'compare prices', 'worth the money', 'save money'] },
      { ru: 'Дорогая покупка', en: 'A big purchase', words: ['expensive', 'warranty', 'quality', 'save up for', 'make a choice', 'value for money', 'pay in instalments', 'long-term'] },
      { ru: 'Импульсивные покупки', en: 'Impulse buying', words: ['impulse buy', 'checkout', 'unnecessary', 'regret buying', 'tempting', 'shopping habit', 'resist the urge', 'think twice'] },
    ],
  },
  {
    id: 'holidays',
    ru: 'Праздники и традиции',
    en: 'Holidays & traditions',
    Icon: PartyPopper,
    subtopics: [
      { ru: 'День рождения', en: 'Birthday', words: ['birthday party', 'birthday cake', 'candles', 'guests', 'make a wish', 'blow out candles', 'celebrate together', 'birthday present'] },
      { ru: 'Новый год', en: 'New Year', words: ['New Year\'s Eve', 'midnight', 'fireworks', 'resolution', 'decorate the house', 'festive mood', 'count down', 'stay up late'] },
      { ru: 'Подарки', en: 'Gifts', words: ['gift idea', 'wrapping paper', 'surprise', 'wish list', 'thoughtful present', 'exchange gifts', 'choose for someone', 'open a present'] },
      { ru: 'Семейный праздник', en: 'A family celebration', words: ['relatives', 'family table', 'traditional meal', 'toast', 'gather together', 'special occasion', 'host guests', 'family tradition'] },
      { ru: 'Приглашение', en: 'An invitation', words: ['invitation', 'RSVP', 'bring someone along', 'be available', 'accept an invitation', 'turn down', 'arrive on time', 'celebrate with friends'] },
    ],
  },
  {
    id: 'cafesRestaurants',
    ru: 'Кафе, рестораны и сервис',
    en: 'Cafés, restaurants & service',
    Icon: Coffee,
    subtopics: [
      { ru: 'Заказ в кафе', en: 'Ordering at a café', words: ['menu', 'order a drink', 'still water', 'takeaway cup', 'table service', 'choose a dessert', 'pay by card', 'enjoy your meal'] },
      { ru: 'Бронирование стола', en: 'Booking a table', words: ['make a reservation', 'table for two', 'available time', 'fully booked', 'confirm a booking', 'window table', 'arrive early', 'cancel a reservation'] },
      { ru: 'Хорошее обслуживание', en: 'Good service', words: ['friendly staff', 'attentive', 'quick service', 'helpful', 'polite', 'make a recommendation', 'check on guests', 'leave a tip'] },
      { ru: 'Плохой сервис', en: 'Bad service', words: ['wrong order', 'cold food', 'wait too long', 'complain politely', 'speak to the manager', 'replace a dish', 'poor service', 'ask for a refund'] },
      { ru: 'Любимое место', en: 'A favourite place', words: ['regular customer', 'cosy atmosphere', 'local café', 'quiet corner', 'good coffee', 'reasonable prices', 'meet friends', 'spend an afternoon'] },
    ],
  },
  {
    id: 'beauty',
    ru: 'Красота и уход за собой',
    en: 'Beauty & self-care',
    Icon: Sparkles,
    subtopics: [
      { ru: 'Утренний уход', en: 'Morning skincare', words: ['face wash', 'moisturiser', 'sunscreen', 'mirror', 'skincare routine', 'apply cream', 'fresh face', 'get ready'] },
      { ru: 'Уход за волосами', en: 'Hair care', words: ['shampoo', 'conditioner', 'haircut', 'trim', 'hairdresser', 'make an appointment', 'style your hair', 'change your look'] },
      { ru: 'Отдых и self-care', en: 'Rest & self-care', words: ['self-care', 'take time off', 'have a bath', 'recharge', 'switch off', 'quiet evening', 'treat yourself', 'feel refreshed'] },
      { ru: 'Внешность и уверенность', en: 'Appearance & confidence', words: ['appearance', 'feel confident', 'natural look', 'personal style', 'compare yourself', 'feel comfortable', 'look your best', 'self-image'] },
      { ru: 'Косметика', en: 'Make-up', words: ['lipstick', 'foundation', 'shade', 'make-up bag', 'apply make-up', 'remove make-up', 'beauty product', 'sensitive skin'] },
    ],
  },
  {
    id: 'newsSociety',
    ru: 'Новости и общество',
    en: 'News & society',
    Icon: Newspaper,
    subtopics: [
      { ru: 'Источники новостей', en: 'News sources', words: ['headline', 'news source', 'journalist', 'reliable', 'follow the news', 'news app', 'breaking news', 'check facts'] },
      { ru: 'Местное событие', en: 'A local event', words: ['local event', 'announcement', 'community', 'public space', 'take place', 'attend an event', 'city news', 'crowded streets'] },
      { ru: 'Хорошие новости', en: 'Good news', words: ['positive news', 'achievement', 'improve', 'hopeful', 'good cause', 'celebrate success', 'share good news', 'make progress'] },
      { ru: 'Недоверие к информации', en: 'Distrust of information', words: ['rumour', 'fake news', 'bias', 'evidence', 'trusted source', 'verify information', 'misleading', 'question a claim'] },
      { ru: 'Общественная проблема', en: 'A social issue', words: ['social issue', 'public service', 'government', 'affect people', 'raise awareness', 'propose a solution', 'community project', 'make a change'] },
    ],
  },
  {
    id: 'environment',
    ru: 'Экология',
    en: 'Environment',
    Icon: Leaf,
    subtopics: [
      { ru: 'Сортировка мусора', en: 'Sorting rubbish', words: ['recycling bin', 'separate waste', 'paper', 'glass', 'plastic', 'food waste', 'throw away', 'recycle properly'] },
      { ru: 'Одноразовый пластик', en: 'Single-use plastic', words: ['single-use plastic', 'packaging', 'reusable bottle', 'cloth bag', 'plastic-free', 'avoid waste', 'refill', 'bring your own'] },
      { ru: 'Экологичный транспорт', en: 'Eco-friendly transport', words: ['public transport', 'ride a bike', 'walk instead', 'emissions', 'fuel', 'electric car', 'traffic pollution', 'reduce your footprint'] },
      { ru: 'Экономия дома', en: 'Saving resources at home', words: ['save electricity', 'switch off lights', 'water bill', 'energy efficient', 'turn off the tap', 'lower heating', 'waste less', 'household habits'] },
      { ru: 'Климат', en: 'Climate', words: ['climate change', 'global warming', 'extreme weather', 'drought', 'responsibility', 'sustainable', 'future generations', 'take action'] },
    ],
  },
  {
    id: 'science',
    ru: 'Наука и космос',
    en: 'Science & space',
    Icon: Rocket,
    subtopics: [
      { ru: 'Научное открытие', en: 'A scientific discovery', words: ['discovery', 'scientist', 'research', 'evidence', 'experiment', 'result', 'make a breakthrough', 'prove a theory'] },
      { ru: 'Эксперимент', en: 'An experiment', words: ['laboratory', 'test a hypothesis', 'measure', 'data', 'sample', 'repeat an experiment', 'unexpected result', 'draw a conclusion'] },
      { ru: 'Космос', en: 'Space', words: ['planet', 'galaxy', 'astronaut', 'rocket', 'orbit', 'telescope', 'space station', 'launch into space'] },
      { ru: 'Медицина и исследования', en: 'Medicine & research', words: ['vaccine', 'clinical trial', 'treatment', 'disease', 'develop a drug', 'medical research', 'side effect', 'save lives'] },
      { ru: 'Научные мифы', en: 'Science myths', words: ['claim', 'fact', 'theory', 'doubt', 'source', 'scientific method', 'believe a myth', 'check the evidence'] },
    ],
  },
  {
    id: 'history',
    ru: 'История и прошлое',
    en: 'History & the past',
    Icon: Landmark,
    subtopics: [
      { ru: 'История семьи', en: 'Family history', words: ['ancestor', 'family tree', 'generation', 'old photograph', 'family story', 'trace your roots', 'pass down', 'distant relative'] },
      { ru: 'История города', en: 'The city\'s history', words: ['monument', 'old town', 'historical building', 'century', 'local history', 'preserve a place', 'change over time', 'landmark'] },
      { ru: 'Музей истории', en: 'A history museum', words: ['artefact', 'exhibition', 'display case', 'guide', 'ancient object', 'learn about', 'historical period', 'museum ticket'] },
      { ru: 'Историческая личность', en: 'A historical figure', words: ['leader', 'ruler', 'achievement', 'influence', 'make history', 'famous for', 'historical figure', 'change society'] },
      { ru: 'Прошлое и настоящее', en: 'Past & present', words: ['in the past', 'nowadays', 'different from before', 'social change', 'progress', 'traditional', 'modern life', 'compare periods'] },
    ],
  },
  {
    id: 'art',
    ru: 'Искусство и культура',
    en: 'Art & culture',
    Icon: Brush,
    subtopics: [
      { ru: 'Живопись', en: 'Painting', words: ['painting', 'artist', 'brush', 'canvas', 'colour palette', 'abstract art', 'gallery', 'create art'] },
      { ru: 'Театр', en: 'Theatre', words: ['stage', 'audience', 'actor', 'performance', 'applause', 'play a role', 'theatre ticket', 'live theatre'] },
      { ru: 'Музей искусства', en: 'An art museum', words: ['exhibition', 'masterpiece', 'art collection', 'curator', 'sculpture', 'admire', 'modern art', 'guided visit'] },
      { ru: 'Концерт классической музыки', en: 'A classical concert', words: ['orchestra', 'conductor', 'programme', 'concert hall', 'instrument', 'live performance', 'audience member', 'standing ovation'] },
      { ru: 'Современное искусство', en: 'Contemporary art', words: ['installation', 'message', 'unusual', 'interpret', 'opinion', 'creative idea', 'make you think', 'artistic choice'] },
    ],
  },
  {
    id: 'socialMedia',
    ru: 'Социальные сети и интернет',
    en: 'Social media & the internet',
    Icon: Share2,
    subtopics: [
      { ru: 'Лента соцсети', en: 'Your social feed', words: ['feed', 'post', 'algorithm', 'scroll', 'suggested content', 'go viral', 'screen time', 'see an update'] },
      { ru: 'Блогеры', en: 'Influencers', words: ['influencer', 'follower', 'sponsored post', 'audience', 'subscribe', 'create content', 'brand deal', 'online personality'] },
      { ru: 'Комментарии', en: 'Comments', words: ['leave a comment', 'reply', 'reaction', 'rude comment', 'online argument', 'block someone', 'report a post', 'share an opinion'] },
      { ru: 'Экранное время', en: 'Screen time', words: ['notification', 'distraction', 'take a break', 'set a limit', 'waste time', 'focus mode', 'check your phone', 'digital habit'] },
      { ru: 'Цифровой детокс', en: 'Digital detox', words: ['go offline', 'delete an app', 'switch off notifications', 'spend time outside', 'avoid social media', 'feel present', 'take a break', 'reconnect with people'] },
    ],
  },
  {
    id: 'dreamsGoals',
    ru: 'Мечты, цели и будущее',
    en: 'Dreams, goals & the future',
    Icon: Target,
    subtopics: [
      { ru: 'План на год', en: 'A yearly plan', words: ['set a goal', 'New Year\'s resolution', 'make progress', 'deadline', 'plan ahead', 'track results', 'stay consistent', 'achieve a goal'] },
      { ru: 'Большая мечта', en: 'A big dream', words: ['dream job', 'travel the world', 'opportunity', 'take a risk', 'possible future', 'work towards', 'long-term goal', 'make it happen'] },
      { ru: 'Новая привычка', en: 'A new habit', words: ['daily routine', 'reminder', 'motivation', 'build a habit', 'stick to it', 'give up', 'small step', 'see results'] },
      { ru: 'Страх будущего', en: 'Fear of the future', words: ['uncertainty', 'worry about', 'feel prepared', 'make a backup plan', 'change quickly', 'out of control', 'hope for the best', 'deal with fear'] },
      { ru: 'Жизнь через десять лет', en: 'Life in ten years', words: ['prediction', 'technology', 'lifestyle', 'career path', 'likely to', 'imagine yourself', 'look forward to', 'future self'] },
    ],
  },
  {
    id: 'childhood',
    ru: 'Детство и воспоминания',
    en: 'Childhood & memories',
    Icon: Baby,
    subtopics: [
      { ru: 'Школа', en: 'School', words: ['classmate', 'school uniform', 'favourite teacher', 'break time', 'homework', 'classroom', 'school trip', 'remember clearly'] },
      { ru: 'Игры во дворе', en: 'Playing outside', words: ['playground', 'hide-and-seek', 'swing', 'make up rules', 'play outside', 'after school', 'childhood game', 'call a friend'] },
      { ru: 'Лето', en: 'Summer', words: ['grandparents', 'countryside', 'summer camp', 'river', 'stay up late', 'carefree', 'sunny day', 'happy memory'] },
      { ru: 'Любимая еда', en: 'Favourite food', words: ['homemade food', 'sweet treat', 'family recipe', 'smell of food', 'favourite snack', 'cook together', 'special meal', 'remind you of'] },
      { ru: 'Старые вещи', en: 'Old belongings', words: ['photo album', 'old toy', 'cassette tape', 'keep a souvenir', 'childhood room', 'throw away', 'sentimental', 'feel nostalgic'] },
    ],
  },
  {
    id: 'personality',
    ru: 'Личность и привычки',
    en: 'Personality & habits',
    Icon: Repeat,
    subtopics: [
      { ru: 'Сильные стороны', en: 'Strengths', words: ['curious', 'patient', 'organised', 'reliable', 'creative', 'good at', 'deal with', 'proud of'] },
      { ru: 'Прокрастинация', en: 'Procrastination', words: ['put off', 'distraction', 'last minute', 'avoid a task', 'feel guilty', 'make a list', 'focus on', 'get started'] },
      { ru: 'Решения', en: 'Decisions', words: ['choice', 'option', 'trust your instinct', 'think it over', 'make up your mind', 'regret', 'consequence', 'right decision'] },
      { ru: 'Интроверсия', en: 'Introversion', words: ['quiet person', 'social energy', 'spend time alone', 'recharge', 'crowded place', 'small group', 'feel comfortable', 'open up'] },
      { ru: 'Изменения себя', en: 'Changing yourself', words: ['personal growth', 'make an effort', 'bad habit', 'improve yourself', 'fail at first', 'keep trying', 'become better', 'take time'] },
    ],
  },
  {
    id: 'communication',
    ru: 'Общение и язык',
    en: 'Communication & language',
    Icon: Languages,
    subtopics: [
      { ru: 'Изучение языка', en: 'Learning a language', words: ['vocabulary', 'grammar rule', 'fluent', 'practise speaking', 'make mistakes', 'learn by heart', 'understand native speakers', 'improve steadily'] },
      { ru: 'Акцент', en: 'Accent & pronunciation', words: ['pronunciation', 'sound natural', 'understandable', 'repeat after', 'stress a word', 'accent', 'difficult sound', 'speak clearly'] },
      { ru: 'Small talk', en: 'Small talk', words: ['start a conversation', 'awkward silence', 'ask about', 'make a comment', 'stranger', 'keep it going', 'polite question', 'common topic'] },
      { ru: 'Переписка', en: 'Texting', words: ['send a message', 'reply late', 'tone of voice', 'emoji', 'read a message', 'leave on read', 'misunderstand', 'text back'] },
      { ru: 'Культурные различия', en: 'Cultural differences', words: ['personal space', 'direct', 'polite', 'rude', 'body language', 'social rule', 'mean something else', 'cultural difference'] },
    ],
  },
  {
    id: 'opinions',
    ru: 'Мнения и обсуждения',
    en: 'Opinions & discussions',
    Icon: MessageCircle,
    subtopics: [
      { ru: 'Согласие', en: 'Agreeing', words: ['I agree', 'exactly', 'good point', 'makes sense', 'I feel the same', 'support an idea', 'fair enough', 'you are right'] },
      { ru: 'Несогласие', en: 'Disagreeing', words: ['I disagree', 'however', 'from my perspective', 'not convinced', 'unfair', 'see it differently', 'argue against', 'respect your view'] },
      { ru: 'Плюсы и минусы', en: 'Pros & cons', words: ['advantage', 'disadvantage', 'benefit', 'drawback', 'weigh up', 'worth it', 'practical', 'downside'] },
      { ru: 'Популярные вещи', en: 'Popular things', words: ['trend', 'overrated', 'popular', 'everyone talks about', 'personal taste', 'prefer', 'try once', 'not for me'] },
      { ru: 'Правила', en: 'Rules', words: ['freedom', 'responsibility', 'necessary', 'allow', 'forbid', 'follow a rule', 'break a rule', 'make sense'] },
    ],
  },
  {
    id: 'problems',
    ru: 'Проблемы и решения',
    en: 'Problems & solutions',
    Icon: LifeBuoy,
    subtopics: [
      { ru: 'Опоздание', en: 'Being late', words: ['be late', 'traffic jam', 'miss a bus', 'excuse', 'apologise', 'wait for', 'on the way', 'arrive eventually'] },
      { ru: 'Потерянная вещь', en: 'A lost item', words: ['wallet', 'keys', 'look for', 'lost and found', 'report it', 'replace', 'luckily', 'turn up'] },
      { ru: 'Просьба о помощи', en: 'Asking for help', words: ['do a favour', 'available', 'urgent', 'help out', 'appreciate it', 'ask someone', 'lend a hand', 'thanks in advance'] },
      { ru: 'Совет', en: 'Giving advice', words: ['suggestion', 'recommend', 'sensible', 'in your shoes', 'think about', 'give advice', 'useful tip', 'work out'] },
      { ru: 'Сложный выбор', en: 'A hard choice', words: ['risk', 'consequence', 'options', 'pros and cons', 'take a chance', 'regret later', 'make a decision', 'solve a problem'] },
    ],
  },
  {
    id: 'customerService',
    ru: 'Работа с клиентами и сервис',
    en: 'Customer service',
    Icon: Headset,
    subtopics: [
      { ru: 'Возврат товара', en: 'Returning an item', words: ['refund', 'faulty item', 'receipt', 'return policy', 'exchange', 'original packaging', 'money back', 'customer service'] },
      { ru: 'Доставка', en: 'Delivery', words: ['courier', 'tracking number', 'delivery address', 'parcel', 'delayed', 'missed delivery', 'doorstep', 'reschedule'] },
      { ru: 'Техподдержка', en: 'Tech support', words: ['support team', 'submit a request', 'issue', 'response time', 'account details', 'solve a problem', 'contact us', 'helpful agent'] },
      { ru: 'Жалоба', en: 'A complaint', words: ['complaint', 'unacceptable', 'speak to a manager', 'poor quality', 'inconvenience', 'resolve an issue', 'compensation', 'formal email'] },
      { ru: 'Хороший сервис', en: 'Great service', words: ['friendly staff', 'efficient', 'helpful', 'exceed expectations', 'quick response', 'recommend a company', 'loyal customer', 'positive review'] },
    ],
  },
  {
    id: 'lifeChanges',
    ru: 'Жизненные перемены',
    en: 'Life changes',
    Icon: TrendingUp,
    subtopics: [
      { ru: 'Переезд', en: 'Moving to a new city', words: ['new city', 'moving boxes', 'unfamiliar place', 'rent a flat', 'meet neighbours', 'settle in', 'miss home', 'fresh start'] },
      { ru: 'Новая работа', en: 'Starting a new job', words: ['first day', 'new role', 'introduce yourself', 'learn the ropes', 'nervous', 'adapt quickly', 'team culture', 'probation period'] },
      { ru: 'Новое хобби', en: 'A new hobby', words: ['beginner', 'equipment', 'sign up for', 'first attempt', 'practise regularly', 'improve slowly', 'give up', 'enjoy the process'] },
      { ru: 'Расставание', en: 'A breakup', words: ['relationship', 'break up', 'difficult time', 'emotional support', 'move on', 'keep in touch', 'learn from', 'feel better'] },
      { ru: 'Неожиданный поворот', en: 'An unexpected turn', words: ['surprise', 'change of plans', 'opportunity', 'deal with', 'adapt', 'not expect', 'take it as it comes', 'turn out well'] },
    ],
  },
  {
    id: 'safety',
    ru: 'Безопасность и чрезвычайные ситуации',
    en: 'Safety & emergencies',
    Icon: ShieldAlert,
    subtopics: [
      { ru: 'Потерянный телефон', en: 'A lost phone', words: ['lock your phone', 'find my phone', 'location', 'block an account', 'stolen', 'contact provider', 'change password', 'stay calm'] },
      { ru: 'Несчастный случай', en: 'An accident', words: ['accident', 'injury', 'ambulance', 'emergency', 'first aid', 'call for help', 'hurt yourself', 'safe place'] },
      { ru: 'Безопасность в поездке', en: 'Safety while travelling', words: ['passport', 'safe area', 'stranger', 'watch out', 'travel document', 'keep valuables', 'emergency contact', 'local police'] },
      { ru: 'Безопасность дома', en: 'Safety at home', words: ['smoke alarm', 'lock the door', 'spare key', 'fire extinguisher', 'check the gas', 'power cut', 'safe at home', 'call a plumber'] },
      { ru: 'Аптека', en: 'At the pharmacy', words: ['pharmacy', 'painkiller', 'prescription', 'allergic reaction', 'describe symptoms', 'ask a pharmacist', 'medicine', 'dosage'] },
    ],
  },
  {
    id: 'dating',
    ru: 'Знакомства и отношения',
    en: 'Dating & relationships',
    Icon: Heart,
    subtopics: [
      { ru: 'Первое свидание', en: 'A first date', words: ['date', 'nervous', 'first impression', 'meet for coffee', 'get to know', 'good conversation', 'chemistry', 'text back'] },
      { ru: 'Онлайн-знакомства', en: 'Online dating', words: ['profile', 'match', 'swipe', 'send a message', 'photo', 'common interests', 'meet in person', 'dating app'] },
      { ru: 'Комплименты', en: 'Compliments', words: ['genuine compliment', 'kind words', 'notice something', 'make someone smile', 'flattering', 'say thank you', 'feel appreciated', 'be sincere'] },
      { ru: 'Границы', en: 'Boundaries', words: ['personal boundary', 'feel comfortable', 'respect a choice', 'say no', 'honest conversation', 'trust', 'cross a line', 'communicate clearly'] },
      { ru: 'Совместные планы', en: 'Plans together', words: ['partner', 'compromise', 'make plans', 'future together', 'support each other', 'decide together', 'shared goal', 'quality time'] },
    ],
  },
  {
    id: 'funny',
    ru: 'Смешное и странное',
    en: 'Funny & strange stories',
    Icon: Laugh,
    subtopics: [
      { ru: 'Неловкий момент', en: 'An awkward moment', words: ['embarrassing', 'awkward', 'blush', 'say the wrong thing', 'silent room', 'laugh it off', 'happen to me', 'wish to disappear'] },
      { ru: 'Совпадение', en: 'A coincidence', words: ['coincidence', 'by chance', 'unexpectedly', 'familiar face', 'small world', 'run into', 'cannot believe it', 'realise later'] },
      { ru: 'Странная привычка', en: 'A weird habit', words: ['weird habit', 'secretly', 'admit', 'do without thinking', 'everyday thing', 'harmless', 'odd', 'nobody knows'] },
      { ru: 'Смешной провал', en: 'A funny fail', words: ['disaster', 'ridiculous', 'go wrong', 'make a mess', 'survive it', 'tell the story', 'laugh afterwards', 'lesson learned'] },
      { ru: 'Необычное правило', en: 'An unusual rule', words: ['bizarre', 'forbidden', 'allowed', 'make sense', 'strict rule', 'break the rule', 'unexpected', 'explain why'] },
    ],
  },
  {
    id: 'cinemaMedia',
    ru: 'Кино и медиа',
    en: 'Cinema & media',
    Icon: Clapperboard,
    subtopics: [
      { ru: 'Документальные фильмы', en: 'Documentaries', words: ['documentary', 'real story', 'narrator', 'interview', 'learn about', 'eye-opening', 'subject matter', 'based on facts'] },
      { ru: 'Подкасты', en: 'Podcasts', words: ['podcast episode', 'host', 'guest speaker', 'listen on the way', 'subscribe', 'interesting topic', 'background listening', 'recommend an episode'] },
      { ru: 'Новости в видео', en: 'Video news', words: ['news channel', 'short clip', 'report', 'live stream', 'watch online', 'presenter', 'latest update', 'visual story'] },
      { ru: 'Реклама', en: 'Advertising', words: ['advertisement', 'commercial', 'target audience', 'catchy slogan', 'skip an ad', 'brand message', 'persuasive', 'annoying'] },
      { ru: 'Создание видео', en: 'Making videos', words: ['record a video', 'edit footage', 'camera angle', 'upload', 'thumbnail', 'viewer', 'share online', 'tell a story'] },
    ],
  },
  {
    id: 'worldCuisine',
    ru: 'Еда разных стран',
    en: 'World cuisine',
    Icon: ChefHat,
    subtopics: [
      { ru: 'Национальная кухня', en: 'National cuisine', words: ['local cuisine', 'traditional dish', 'street food', 'regional recipe', 'authentic', 'taste unfamiliar', 'food culture', 'try for the first time'] },
      { ru: 'Завтрак', en: 'Breakfast', words: ['scrambled eggs', 'oatmeal', 'toast', 'coffee shop', 'skip breakfast', 'filling meal', 'morning routine', 'eat on the go'] },
      { ru: 'Выпечка', en: 'Baking', words: ['bakery', 'fresh bread', 'pastry', 'dough', 'sweet filling', 'oven', 'smell delicious', 'bake from scratch'] },
      { ru: 'Напитки', en: 'Drinks', words: ['still water', 'sparkling water', 'herbal tea', 'coffee beans', 'order a drink', 'iced coffee', 'taste bitter', 'stay hydrated'] },
      { ru: 'Готовка для гостей', en: 'Cooking for guests', words: ['dinner party', 'serve a meal', 'set the table', 'dietary requirement', 'cook in advance', 'main course', 'dessert', 'leftovers'] },
    ],
  },
  {
    id: 'personalFinance',
    ru: 'Домашние финансы',
    en: 'Personal finances',
    Icon: PiggyBank,
    subtopics: [
      { ru: 'Оплата счетов', en: 'Paying bills', words: ['utility bill', 'due date', 'electricity', 'internet bill', 'pay online', 'monthly payment', 'late fee', 'keep a record'] },
      { ru: 'Накопления', en: 'Savings', words: ['savings account', 'put aside', 'emergency fund', 'financial goal', 'save regularly', 'interest rate', 'rainy day', 'build savings'] },
      { ru: 'Банковская карта', en: 'Bank card', words: ['bank card', 'contactless payment', 'cash machine', 'pin code', 'transaction', 'card limit', 'payment failed', 'check balance'] },
      { ru: 'Заём', en: 'Borrowing money', words: ['borrow money', 'lend money', 'pay back', 'interest', 'monthly instalment', 'debt', 'afford repayment', 'financial advice'] },
      { ru: 'План покупки', en: 'Planning a purchase', words: ['compare prices', 'make a budget', 'save up', 'essential item', 'wish list', 'best value', 'wait for a sale', 'make a purchase'] },
    ],
  },
  {
    id: 'foodHealthFitness',
    ru: 'Еда, здоровье и спорт',
    en: 'Food, health & fitness',
    Icon: Dumbbell,
    subtopics: [
      { ru: 'После тренировки', en: 'After a workout', words: ['protein shake', 'sore muscles', 'drink water', 'recover', 'healthy snack', 'stretch afterwards', 'energy level', 'refuel'] },
      { ru: 'Рацион', en: 'Diet', words: ['meal plan', 'balanced meal', 'vegetables', 'whole grains', 'protein source', 'portion control', 'eat regularly', 'nutritious'] },
      { ru: 'Спортивная мотивация', en: 'Fitness motivation', words: ['fitness goal', 'training partner', 'skip a workout', 'keep a routine', 'progress photo', 'challenge yourself', 'stay motivated', 'small win'] },
      { ru: 'Отдых', en: 'Rest', words: ['rest day', 'avoid burnout', 'listen to your body', 'sleep well', 'take it easy', 'physical recovery', 'overtrain', 'feel refreshed'] },
      { ru: 'Полезные перекусы', en: 'Healthy snacks', words: ['fruit bowl', 'nuts', 'yoghurt', 'avoid sugar', 'quick snack', 'hungry between meals', 'pack a snack', 'choose wisely'] },
    ],
  },
  {
    id: 'entrepreneurship',
    ru: 'Предпринимательство',
    en: 'Entrepreneurship',
    Icon: Lightbulb,
    subtopics: [
      { ru: 'Идея бизнеса', en: 'A business idea', words: ['business idea', 'solve a problem', 'target customer', 'market need', 'start small', 'test an idea', 'potential', 'take a risk'] },
      { ru: 'Команда', en: 'The team', words: ['co-founder', 'hire someone', 'role in a team', 'shared vision', 'divide tasks', 'trust a partner', 'team meeting', 'work together'] },
      { ru: 'Клиенты', en: 'Customers', words: ['customer needs', 'feedback', 'loyal client', 'target audience', 'attract customers', 'word of mouth', 'customer journey', 'build trust'] },
      { ru: 'Продажи', en: 'Sales', words: ['offer a service', 'price a product', 'sales pitch', 'negotiate', 'close a deal', 'free trial', 'purchase decision', 'make a sale'] },
      { ru: 'Неудача', en: 'Failure', words: ['business failure', 'learn from mistakes', 'lose money', 'try again', 'change direction', 'hard lesson', 'take responsibility', 'bounce back'] },
    ],
  },
  {
    id: 'careerGrowth',
    ru: 'Учёба и профессиональный рост',
    en: 'Learning & career growth',
    Icon: Award,
    subtopics: [
      { ru: 'Обратная связь', en: 'Feedback', words: ['feedback', 'constructive criticism', 'strengths', 'area to improve', 'specific example', 'accept feedback', 'make changes', 'learn from it'] },
      { ru: 'Презентация', en: 'A presentation', words: ['presentation', 'slide', 'audience', 'speak clearly', 'main point', 'practise beforehand', 'answer questions', 'make an impression'] },
      { ru: 'Новая квалификация', en: 'A new qualification', words: ['certificate', 'qualification', 'professional course', 'gain experience', 'upgrade skills', 'career change', 'study part-time', 'valuable skill'] },
      { ru: 'Наставник', en: 'A mentor', words: ['mentor', 'guidance', 'ask for advice', 'share experience', 'career path', 'learn from someone', 'support', 'role model'] },
      { ru: 'Продуктивность', en: 'Productivity', words: ['prioritise tasks', 'time block', 'deep work', 'avoid distraction', 'to-do list', 'realistic goal', 'take breaks', 'get things done'] },
    ],
  },
  {
    id: 'dailyRoutine',
    ru: 'Режим дня и время',
    en: 'Daily routine & time',
    Icon: Timer,
    subtopics: [
      { ru: 'Планирование дня', en: 'Planning the day', words: ['calendar', 'schedule', 'appointment', 'reminder', 'make time', 'plan ahead', 'move a meeting', 'free slot'] },
      { ru: 'Нехватка времени', en: 'Not enough time', words: ['too busy', 'run out of time', 'put off', 'rush around', 'tight schedule', 'make room for', 'time pressure', 'manage time'] },
      { ru: 'Пунктуальность', en: 'Punctuality', words: ['on time', 'running late', 'set an alarm', 'leave early', 'wait for someone', 'apologise for being late', 'estimate time', 'punctual'] },
      { ru: 'Баланс жизни', en: 'Work-life balance', words: ['work-life balance', 'personal time', 'switch off', 'set boundaries', 'take leave', 'avoid overtime', 'family time', 'feel balanced'] },
      { ru: 'Время года', en: 'Time of year', words: ['this time of year', 'daylight hours', 'seasonal routine', 'annual event', 'once a week', 'every other day', 'lately', 'in advance'] },
    ],
  },
  {
    id: 'travelCulture',
    ru: 'Путешествия и культура',
    en: 'Travel & culture',
    Icon: Compass,
    subtopics: [
      { ru: 'Общение за границей', en: 'Communicating abroad', words: ['local language', 'phrasebook', 'ask for directions', 'polite phrase', 'language barrier', 'make yourself understood', 'translate', 'friendly local'] },
      { ru: 'Культурный этикет', en: 'Cultural etiquette', words: ['table manners', 'dress appropriately', 'local custom', 'show respect', 'take off shoes', 'greet someone', 'public behaviour', 'cultural norm'] },
      { ru: 'Сувениры', en: 'Souvenirs', words: ['souvenir shop', 'handmade gift', 'local product', 'bring back', 'travel memory', 'postcard', 'pack carefully', 'overpriced'] },
      { ru: 'Поездка в одиночку', en: 'Travelling solo', words: ['solo traveller', 'meet people', 'feel independent', 'safety first', 'change plans', 'own pace', 'book a hostel', 'enjoy solitude'] },
      { ru: 'Бюджет путешествия', en: 'Travel budget', words: ['cheap flight', 'travel deal', 'hostel room', 'exchange rate', 'spend wisely', 'free attraction', 'daily budget', 'travel cheaply'] },
    ],
  },
  {
    id: 'emotions',
    ru: 'Ежедневные эмоции',
    en: 'Everyday emotions',
    Icon: Smile,
    subtopics: [
      { ru: 'Радость', en: 'Joy', words: ['feel excited', 'good mood', 'pleasant surprise', 'look forward to', 'smile all day', 'celebrate', 'little joy', 'make my day'] },
      { ru: 'Разочарование', en: 'Disappointment', words: ['disappointed', 'expect more', 'let down', 'not work out', 'frustrating', 'change plans', 'feel annoyed', 'get over it'] },
      { ru: 'Гордость', en: 'Pride', words: ['proud of', 'achievement', 'hard work', 'reach a goal', 'deserve it', 'tell someone', 'recognise effort', 'feel accomplished'] },
      { ru: 'Злость', en: 'Anger', words: ['angry', 'lose patience', 'calm down', 'unfair situation', 'speak up', 'take a breath', 'avoid an argument', 'cool off'] },
      { ru: 'Тревога', en: 'Anxiety', words: ['worried', 'overthink', 'uncertainty', 'need reassurance', 'worst-case scenario', 'take a break', 'talk to someone', 'feel calmer'] },
    ],
  },
  {
    id: 'personalPath',
    ru: 'Мечты и личный путь',
    en: 'Dreams & personal path',
    Icon: Mountain,
    subtopics: [
      { ru: 'Идеальная жизнь', en: 'The ideal life', words: ['ideal lifestyle', 'live abroad', 'meaningful work', 'free time', 'peaceful home', 'financial freedom', 'imagine a future', 'personal choice'] },
      { ru: 'Большое решение', en: 'A big decision', words: ['turning point', 'take a leap', 'leave comfort zone', 'new direction', 'weigh options', 'trust yourself', 'major change', 'no turning back'] },
      { ru: 'Список желаний', en: 'A bucket list', words: ['bucket list', 'dream destination', 'learn a skill', 'once in a lifetime', 'make it happen', 'future plan', 'exciting goal', 'life experience'] },
      { ru: 'Личный успех', en: 'Personal success', words: ['definition of success', 'personal value', 'make progress', 'feel fulfilled', 'compare yourself', 'own path', 'be proud', 'long-term effort'] },
      { ru: 'Наследие', en: 'Legacy', words: ['make an impact', 'help others', 'be remembered', 'positive change', 'pass on knowledge', 'future generation', 'important cause', 'leave a mark'] },
    ],
  },
] as const satisfies readonly Topic[];

export type TopicId = (typeof TOPICS)[number]['id'];

/** Localised topic/subtopic titles — данные хранят оба языка сразу, без отдельного i18n-словаря. */
export function localizedTopicTitle(topic: Pick<Topic, 'ru' | 'en'>, lang: string): string {
  return lang === 'ru' ? topic.ru : topic.en;
}

export function localizedSubtopicTitle(subtopic: Pick<Subtopic, 'ru' | 'en'>, lang: string): string {
  return lang === 'ru' ? subtopic.ru : subtopic.en;
}

/**
 * Единица лексики в чеклисте «Лексика» — слово/фраза и, если есть, перевод.
 * У тем с рулетки перевода пока нет (слова английские, см. `Subtopic.words`),
 * у ролевых игр он приходит с сервера (см. `Dialogue.roleplayVocabulary`).
 */
export interface VocabularyWord {
  word: string;
  translation?: string;
}

/** Тема, подтема и её лексика — всё, что нужно показать по теме с рулетки. */
export interface TopicVocabulary {
  topicLabel: string;
  subtopicLabel: string;
  words: readonly VocabularyWord[];
}

/**
 * Тема/подтема идентификаторами каталога → локализованные названия и список
 * лексики. Идентификаторы приходят из двух мест: адреса страницы (переход с
 * рулетки, см. `widgets/topic-picker-entry` → `ChatPage`) и самого разговора,
 * загруженного с сервера (`Dialogue.topic` — возврат в разговор из истории,
 * когда в адресе их уже нет). Неизвестный id или индекс за границей списка —
 * `undefined`: каталог мог поехать с тех пор, как разговор был начат, и это
 * не повод падать.
 */
export function resolveTopicVocabulary(
  topicId: string | undefined,
  subtopicIndex: number | undefined,
  lang: string,
): TopicVocabulary | undefined {
  if (!topicId || subtopicIndex === undefined) return undefined;
  const topic = TOPICS.find((item) => item.id === topicId);
  const subtopic = topic?.subtopics[subtopicIndex];
  if (!topic || !subtopic) return undefined;
  return {
    topicLabel: localizedTopicTitle(topic, lang),
    subtopicLabel: localizedSubtopicTitle(subtopic, lang),
    // Слова тем — пока только английские, без перевода (см. `VocabularyWord`).
    words: subtopic.words.map((word) => ({ word })),
  };
}
